import React, {
  Fragment,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { map, prop, sortBy } from "ramda";
import { UserContext } from "UserContext";
import {
  Grid,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@material-ui/core";
import { fetchReport, fetchReportPost, saveReportResponse } from "fetchUtils";
import ReportsPickerCard from "./ReportsPickerCard";
import ReportsCard from "./ReportsCard";
import OrganizationReportsPickerCard from "./OrganizationReportsPickerCard";
import LoadingButton from "./LoadingButton";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { validateDateRange } from "utils";

const getModuleUrl = orgId =>
  `${process.env.REACT_APP_API_BASE_URL}/organizations/${orgId}/subscriptions/modules?limit=all`;

const getModuleReportUrl = props =>
  `${
    process.env.REACT_APP_API_BASE_URL
  }/reports/usage/module/report.csv?org-id=${
    props.selectedOrganization
  }&media-id=${props.selectedModule}&start=${isoDate(
    props.startDate
  )}&end=${isoDate(props.endDate)}`;

const getAnthologyReportUrl = props =>
  `${
    process.env.REACT_APP_API_BASE_URL
  }/reports/usage/anthology/report.csv?org-id=${
    props.selectedOrganization
  }&media-id=${props.selectedModule}&start=${isoDate(
    props.startDate
  )}&end=${isoDate(props.endDate)}`;

const getOrgContentReportUrl = props =>
  `${
    process.env.REACT_APP_API_BASE_URL
  }/reports/usage/organization-content/report.csv?org-id=${
    props.selectedOrganization
  }&start=${isoDate(props.startDate)}&end=${isoDate(props.endDate)}`;

const fetchModules = (userId, token) =>
  fetch(getModuleUrl(userId), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

const minReportDate = new Date("2016-01-01");

const mapModule = module => ({ id: module.id, name: module.displayName });

const useStyles = makeStyles(theme => ({
  root: {
    background: theme.palette.secondary.light,
    height: "100%",
    overflowX: "hidden",
  },
  exportButton: {
    width: 120,
    height: 40,
    marginBottom: 15,
    float: "right",
    right: 15,
  },
  radioGroup: {
    justifyContent: "space-around",
    marginBottom: 15,
  },
  reportDetailsCard: {
    position: "relative",
    // height: 321,
  },
  reportSelect: {
    width: "100%",
  },

  typeColumn: {
    paddingLeft: 20,
    paddingRight: 20,
    marginBottom: 25,
  },

  dateColumn: {
    paddingLeft: 20,
    paddingRight: 20,
  },

  dateSelect: {
    marginTop: 25,
    marginBottom: 25,
  },

  datePicker: {
    minWidth: 130,
    width: "100%",
  },
}));

const reportTypes = [
  { id: "module", name: "Subscribed Modules Report" },
  { id: "content", name: "Creator Content Report" },
];

const initialState = {
  report: reportTypes[0].id,
  includeAnthologies: false,
  modules: [],
  selectedModule: "ALL",
  showLoading: false,
  selectedOrganization: "",
  startDate: new Date(),
  endDate: new Date(),
  disableExport: false,
};

const isoDate = date => date.toISOString().substr(0, 10);

const UsageReports = () => {
  const classes = useStyles();
  const { token, user } = useContext(UserContext);
  const [state, setState] = useState(initialState);

  const handleStateChange = name => value => {
    console.log(`changing state: ${name} to value: ${value}`);
    setState(s => ({ ...s, [name]: value }));
  };

  const handleDateChange = name => value => {
    let startDate = new Date(state.startDate);
    let endDate = new Date(state.endDate);

    if (name === "startDate") {
      startDate = new Date(value);
    } else {
      endDate = new Date(value);
    }

    setState(s => ({
      ...s,
      [name]: value,
      disableExport: !validateDateRange(startDate, endDate),
    }));
  };

  const handleSelectedOrganization = useCallback(value => {
    handleStateChange("selectedOrganization")(value);
  }, []);

  useEffect(() => {
    if (token && state.selectedOrganization) {
      fetchModules(state.selectedOrganization, token)
        .then(response => response.json())
        .then(modules => map(mapModule, modules))
        .then(modules => sortBy(prop("name"), modules))
        .then(modules => {
          handleStateChange("modules")(modules);
        });
    } else {
      handleStateChange("modules")([]);
    }
  }, [token, state.selectedOrganization]);

  const exportReport = props => {
    handleStateChange("showLoading")(true);

    if (state.report === "module") {
      const url = state.includeAnthologies
        ? getAnthologyReportUrl(props)
        : getModuleReportUrl(props);

      if (state.selectedModule === "ALL") {
        const moduleIds = map(m => m.id, state.modules);
        fetchReportPost(url, props.token, moduleIds)
          .then(response =>
            saveReportResponse(response, `${state.report}-usage-report.csv`)
          )
          .finally(() => handleStateChange("showLoading")(false));
      } else {
        fetchReport(url, props.token)
          .then(response =>
            saveReportResponse(response, `${state.report}-usage-report.csv`)
          )
          .finally(() => handleStateChange("showLoading")(false));
      }
    } else {
      const url = getOrgContentReportUrl(props);

      fetchReport(url, props.token)
        .then(response =>
          saveReportResponse(response, `${state.report}-usage-report.csv`)
        )
        .finally(() => handleStateChange("showLoading")(false));
    }
  };

  return (
    <Fragment>
      <Grid item xs>
        <OrganizationReportsPickerCard
          organizations={user.organizations}
          onOrganizationChange={handleSelectedOrganization}
        ></OrganizationReportsPickerCard>
      </Grid>

      <Grid item xs>
        <ReportsCard cardLabel="REPORT" className={classes.reportDetailsCard}>
          <Grid container direction="column" className={classes.typeColumn}>
            <Grid item>
              <FormControl className={classes.reportSelect}>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={state.report}
                  onChange={e => handleStateChange("report")(e.target.value)}
                >
                  {map(
                    ({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ),
                    reportTypes
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </ReportsCard>
      </Grid>

      {state.report === "module" && (
        <Grid item xs>
          {state.modules.length < 1 ? (
            <div>Loading...</div>
          ) : (
            <ReportsPickerCard
              label="MODULE"
              includeAll={true}
              values={state.modules}
              selectedValue={state.selectedModule}
              onValueChange={handleStateChange("selectedModule")}
            >
              <Grid container direction="column" className={classes.typeColumn}>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.includeAnthologies}
                        onChange={e =>
                          handleStateChange("includeAnthologies")(
                            e.target.checked
                          )
                        }
                        name="includeAnthologies"
                      />
                    }
                    label="Include Anthologieis"
                  />
                </Grid>
              </Grid>
            </ReportsPickerCard>
          )}
        </Grid>
      )}

      <Grid item xs>
        <ReportsCard
          cardLabel="Report Details"
          className={classes.reportDetailsCard}
        >
          <Grid container direction="column" className={classes.dateColumn}>
            <Grid item>
              <Typography>
                Please select a date range not exceeding 6 months
              </Typography>
            </Grid>
            <Grid
              container
              item
              direction="row"
              alignItems="flex-end"
              spacing={1}
              className={classes.dateSelect}
            >
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Grid item xs>
                  <KeyboardDatePicker
                    className={classes.datePicker}
                    margin="normal"
                    id="startDate"
                    label="Start Date"
                    format="MM/dd/yyyy"
                    disableFuture={true}
                    minDate={minReportDate}
                    value={state.startDate}
                    onChange={handleDateChange("startDate")}
                    KeyboardButtonProps={{
                      "aria-label": "start date",
                    }}
                  />
                </Grid>

                <Grid item xs>
                  <KeyboardDatePicker
                    className={classes.datePicker}
                    margin="normal"
                    id="endDate"
                    label="End Date"
                    format="MM/dd/yyyy"
                    disableFuture={true}
                    minDate={minReportDate}
                    value={state.endDate}
                    onChange={handleDateChange("endDate")}
                    KeyboardButtonProps={{
                      "aria-label": "end date",
                    }}
                  />
                </Grid>
              </MuiPickersUtilsProvider>
            </Grid>

            <Grid item align="right">
              <LoadingButton
                variant="contained"
                disableElevation
                className={classes.exportButton}
                onClick={() => exportReport({ token, ...state })}
                disabled={state.disableExport}
                showLoading={state.showLoading}
              >
                EXPORT
              </LoadingButton>
            </Grid>
          </Grid>
        </ReportsCard>
      </Grid>
    </Fragment>
  );
};

export default UsageReports;
